/// <reference path="../../../../typings/index.d.ts" />

import { assign } from 'lodash';
import { format, formatISO } from 'date-fns';

import { AxelDate, AxelType } from '../type';
import { formatInTimeZone, formatInUTC } from '@mode/shared/util-js';

interface DateFormatterSettings {
  ignoreTimezone?: boolean;
}

/**
 * Date
 *
 * Formatter factory that returns templated date formatter
 * @param formatTemplate
 * @param opts
 * @returns {function(AxelDate|Date): (any|string|string)}
 */
export default function date(formatTemplate = '', opts: DateFormatterSettings = {}): (data: AxelType | Date) => any {
  const defaults = { ignoreTimezone: false };
  const settings: DateFormatterSettings = <DateFormatterSettings>assign({}, defaults, opts);

  /**
   * Date Formatter
   * @param data {AxelDate}
   * @todo consolidate and rethink local time conditional
   * @returns {string}
   */
  return function dateFormatter(data: AxelDate | Date) {
    const date = dateValue(data);
    const timestamp = originalValue(data);

    if (isValidDate(data)) {
      if (settings.ignoreTimezone) {
        // parse local time or epoch
        return formatTemplate ? format(date, formatTemplate) : formatISO(date);
      } else if (isLocalTime(timestamp) || isEpoch(timestamp)) {
        // parse local time or epoch as utc
        return formatTemplate ? formatInUTC(date, formatTemplate) : date.toISOString();
      } else {
        if (!formatTemplate) {
          return timestamp;
        }
        // get the UTC time offset from the timestamp
        let timeZone = timestamp.substring(timestamp.length - 6);
        // fall back to zero UTC offset if no offset is present
        if (!timeZone || timeZone.toLowerCase().includes('z')) {
          timeZone = '+00:00';
        }
        return formatInTimeZone(date, formatTemplate, timeZone);
      }
    } else {
      if (timestamp !== null) {
        return 'Invalid Date';
      } else {
        return '';
      }
    }
  };
}

/**
 * Original Value
 *
 * Retrieves the original time value of the date object.
 * Date doesn't retain it's original value so we just
 * return the epoch value so we can assert against it
 * later
 * @todo rethink how we handle {Date} here since it adds unreasonable complexity
 * @param date
 * @returns {any}
 */
function originalValue(date: any) {
  if (isAxelDate(date)) {
    return date.original();
  } else if (isDate(date)) {
    return date.valueOf();
  } else {
    return null;
  }
}

function dateValue(date: AxelDate | Date) {
  if (isAxelDate(date)) {
    return date.valueOf();
  } else {
    return date;
  }
}

function isValidDate(date: AxelDate | Date) {
  if (isAxelDate(date)) {
    return !AxelType.isDirty(date);
  } else {
    return isDate(date);
  }
}

function isLocalTime(stamp) {
  return (
    typeof stamp === 'string' &&
    stamp.search(/[+-][0-9]{4}/) === -1 &&
    stamp.search(/[+-][0-9]{2}:[0-9]{2}/) === -1 &&
    stamp.search(/Z/) === -1
  );
}

function isEpoch(value) {
  return 'number' === typeof value;
}

function isAxelDate(date: any) {
  return date instanceof AxelDate || 'function' === typeof date.isDirty;
}

function isDate(date: any) {
  return date instanceof Date;
}
